/**
 * StylePresetPicker — reusable dropdown for choosing the visual
 * style (3D Pixar, 2D anime, watercolor, etc.) applied to a
 * channel's kit + all its storyboard scenes + thumbnails.
 *
 * Fetches the preset catalog from `/api/style-presets` on first
 * mount. Caches for the component's lifetime (list is <10 items and
 * essentially static — presets only change when we ship code).
 *
 * Props:
 *   value    → current preset key (empty string = default `pixar_3d`)
 *   onChange → (key: string) => void
 *   label    → optional label above the dropdown
 *   testId   → optional data-testid prefix for the container
 */
import { useEffect, useState } from "react";
import { Eye } from "lucide-react";
import { listStylePresets } from "@/lib/api";
import StylePreviewDialog from "@/components/StylePreviewDialog";

let _cached = null;
let _pending = null;

async function loadPresets() {
  if (_cached) return _cached;
  if (!_pending) _pending = listStylePresets().catch(() => []);
  const list = await _pending;
  if (list && list.length) _cached = list;
  return list || [];
}

export default function StylePresetPicker({
  value,
  onChange,
  label = "Visual style",
  helpText = "Applied to the channel banner, avatar, every scene image, and every thumbnail.",
  testId = "style-preset-picker",
}) {
  const [presets, setPresets] = useState(_cached || []);
  const [loading, setLoading] = useState(!_cached);
  const [previewOpen, setPreviewOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!_cached) {
      loadPresets().then((list) => {
        if (!cancelled) {
          setPresets(list);
          setLoading(false);
        }
      });
    }
    return () => { cancelled = true; };
  }, []);

  const activeKey = value || "pixar_3d";
  const active = presets.find((p) => p.key === activeKey);

  return (
    <div data-testid={testId} className="space-y-1.5">
      {label && (
        <label
          className="block font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-400"
          htmlFor={`${testId}-select`}
        >
          {label}
        </label>
      )}
      <select
        id={`${testId}-select`}
        data-testid={`${testId}-select`}
        value={activeKey}
        onChange={(e) => onChange?.(e.target.value)}
        disabled={loading || presets.length === 0}
        className="w-full bg-black border border-zinc-700 text-white px-3 py-2 rounded-sm focus:border-yellow-400 outline-none font-mono text-sm disabled:opacity-40"
      >
        {loading && <option>[recon] loading presets…</option>}
        {!loading && presets.map((p) => (
          <option key={p.key} value={p.key}>
            {p.label}
          </option>
        ))}
      </select>
      {active?.description && (
        <p
          data-testid={`${testId}-description`}
          className="text-[11px] text-zinc-500 font-mono leading-relaxed"
        >
          {active.description}
        </p>
      )}
      {helpText && !active?.description && (
        <p className="text-[11px] text-zinc-500 font-mono">{helpText}</p>
      )}
      <button
        type="button"
        data-testid={`${testId}-preview-btn`}
        onClick={() => setPreviewOpen(true)}
        disabled={loading || presets.length === 0}
        className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] px-4 py-2 border border-yellow-400/60 text-yellow-300 hover:bg-yellow-400 hover:text-black disabled:opacity-40 rounded-sm transition-colors"
      >
        <Eye className="w-3.5 h-3.5" />
        See a sample of this style
      </button>
      <StylePreviewDialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        presetKey={activeKey}
        presetLabel={active?.label || activeKey}
      />
    </div>
  );
}
